import { useQuery } from '@tanstack/react-query';
import { FileText, FileWarning, RotateCcw, Undo2 } from 'lucide-react';

import { api } from '@/lib/api';
import type { RelatorioQualidade } from '@/features/relatorios/tipos';
import { formatarInteiro, formatarPercentual, proporcao } from '@/features/relatorios/formato';
import { GraficoBarras } from '@/components/graficos/barras';
import {
  Tabela,
  TabelaCabecalho,
  TabelaCelula,
  TabelaCorpo,
  TabelaLinha,
  TabelaTitulo,
  TabelaVazia,
} from '@/components/ui/table';
import { CartaoGrafico, Indicador } from './cartoes';
import { EsqueletoBloco } from './esqueleto-bloco';
import { ErroBloco } from './erro-bloco';

export function BlocoQualidade({ consulta }: { consulta: string }) {
  const { data, isPending, error } = useQuery({
    queryKey: ['relatorio', 'qualidade', consulta],
    queryFn: () => api.get<RelatorioQualidade>(`/relatorios/qualidade?${consulta}`),
  });

  if (isPending) return <EsqueletoBloco />;
  if (error) return <ErroBloco erro={error} />;

  const { totais } = data;

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Indicador
          rotulo="Solicitações no período"
          valor={formatarInteiro(totais.solicitacoes)}
          icone={FileText}
          tom="bg-marca-suave text-marca"
        />
        <Indicador
          rotulo="Tiveram pendência"
          valor={formatarInteiro(totais.comPendencia)}
          descricao={proporcao(totais.comPendencia, totais.solicitacoes)}
          icone={FileWarning}
          tom="bg-status-pendencia-fundo text-status-pendencia-texto"
        />
        <Indicador
          rotulo="Foram devolvidas"
          valor={formatarInteiro(totais.devolvidas)}
          descricao={proporcao(totais.devolvidas, totais.solicitacoes)}
          icone={Undo2}
          tom="bg-status-devolvido-fundo text-status-devolvido-texto"
        />
        <Indicador
          rotulo="Reabertas"
          valor={formatarInteiro(totais.reabertas)}
          descricao="Voltaram ao fluxo depois de devolvidas"
          icone={RotateCcw}
          tom="bg-status-lido-fundo text-status-lido-texto"
        />
      </div>

      {/* A pergunta "quem mais teve problema" só é justa em taxa: a unidade
          maior tende a liderar qualquer contagem absoluta. As duas aparecem. */}
      <CartaoGrafico
        titulo="Pendência e devolução por UBS"
        descricao="Conta o que aconteceu no processo, não o status de agora: uma solicitação que teve pendência e depois foi agendada continua contando aqui."
      >
        <Tabela>
          <TabelaCabecalho>
            <TabelaLinha>
              <TabelaTitulo>UBS</TabelaTitulo>
              <TabelaTitulo className="text-right">Solicitações</TabelaTitulo>
              <TabelaTitulo className="text-right">Com pendência</TabelaTitulo>
              <TabelaTitulo className="text-right">Taxa</TabelaTitulo>
              <TabelaTitulo className="text-right">Devolvidas</TabelaTitulo>
              <TabelaTitulo className="text-right">Taxa</TabelaTitulo>
              <TabelaTitulo className="text-right">Reabertas</TabelaTitulo>
            </TabelaLinha>
          </TabelaCabecalho>
          <TabelaCorpo>
            {data.porUbs.length === 0 && (
              <TabelaVazia colunas={7}>Sem solicitações no período.</TabelaVazia>
            )}

            {data.porUbs.map((linha) => (
              <TabelaLinha key={linha.ubsId}>
                <TabelaCelula rotulo="UBS" className="font-medium">
                  {linha.nome}
                </TabelaCelula>
                <TabelaCelula rotulo="Solicitações" className="text-right tabular-nums">
                  {formatarInteiro(linha.total)}
                </TabelaCelula>
                <TabelaCelula rotulo="Com pendência" className="text-right tabular-nums">
                  {formatarInteiro(linha.comPendencia)}
                </TabelaCelula>
                <TabelaCelula rotulo="Taxa de pendência" className="text-right tabular-nums">
                  {formatarPercentual(linha.taxaPendencia)}
                </TabelaCelula>
                <TabelaCelula rotulo="Devolvidas" className="text-right tabular-nums">
                  {formatarInteiro(linha.devolvidas)}
                </TabelaCelula>
                <TabelaCelula rotulo="Taxa de devolução" className="text-right tabular-nums">
                  {formatarPercentual(linha.taxaDevolucao)}
                </TabelaCelula>
                <TabelaCelula rotulo="Reabertas" className="text-right tabular-nums">
                  {formatarInteiro(linha.reabertas)}
                </TabelaCelula>
              </TabelaLinha>
            ))}
          </TabelaCorpo>
        </Tabela>
      </CartaoGrafico>

      <div className="grid gap-5 lg:grid-cols-2">
        <CartaoGrafico
          titulo="Taxa de pendência por UBS"
          descricao="Percentual das solicitações da unidade que passaram por pendência."
        >
          <GraficoBarras
            itens={[...data.porUbs]
              .sort((a, b) => b.taxaPendencia - a.taxaPendencia)
              .map((linha) => ({
                chave: linha.ubsId,
                rotulo: linha.nome,
                valor: linha.taxaPendencia,
                detalhe: `${formatarInteiro(linha.comPendencia)} de ${formatarInteiro(linha.total)}`,
                cor: 'bg-status-pendencia-texto',
              }))}
            formatar={formatarPercentual}
          />
        </CartaoGrafico>

        <CartaoGrafico
          titulo="Taxa de devolução por UBS"
          descricao="Percentual das solicitações da unidade devolvidas à origem."
        >
          <GraficoBarras
            itens={[...data.porUbs]
              .sort((a, b) => b.taxaDevolucao - a.taxaDevolucao)
              .map((linha) => ({
                chave: linha.ubsId,
                rotulo: linha.nome,
                valor: linha.taxaDevolucao,
                detalhe: `${formatarInteiro(linha.devolvidas)} de ${formatarInteiro(linha.total)}`,
                cor: 'bg-status-devolvido-texto',
              }))}
            formatar={formatarPercentual}
          />
        </CartaoGrafico>

        <CartaoGrafico
          titulo="Motivos de devolução mais frequentes"
          descricao="Texto escrito pela Regulação ao devolver; agrupado por repetição exata."
        >
          <GraficoBarras
            vazio="Nenhuma devolução com motivo registrado no período."
            itens={data.motivosDevolucao.map((linha, indice) => ({
              chave: `${indice}-${linha.motivo}`,
              rotulo: linha.motivo,
              valor: linha.total,
              cor: 'bg-status-devolvido-texto',
            }))}
          />
        </CartaoGrafico>

        <CartaoGrafico
          titulo="Pendências mais frequentes"
          descricao="O que a Regulação mais precisou pedir de volta."
        >
          <GraficoBarras
            vazio="Nenhuma pendência registrada no período."
            itens={data.pendenciasFrequentes.map((linha, indice) => ({
              chave: `${indice}-${linha.descricao}`,
              rotulo: linha.descricao,
              valor: linha.total,
              cor: 'bg-status-pendencia-texto',
            }))}
          />
        </CartaoGrafico>
      </div>
    </div>
  );
}
